import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { SaveNoSlotParams } from '../../interfaces/AvailableNoSlots';
import { SlotsDictionary, TopSlot } from '../../interfaces/TopSlot';
import { sendAnalyticsNoSlotsWithError } from '../../services/Analytics';
import { sendAnalyticsSlotsReceived } from '../../services/Analytics';
import NoTopSlotService, { GetNoTopSlotsResult } from '../../services/NoTopSlotService';
import TopSlotService, { GetTopSlotsResult } from '../../services/TopSlotService';

interface GetTopSlotsParams {
  hashId?: string;
  maxDate: Date;
  minDate: Date;
  serviceAppointmentId?: string;
}

const topslotService = new TopSlotService();
const noTopSlotsService = new NoTopSlotService();

export enum TOP_SLOT_QUERY_KEYS {
  BOOK_TOP_SLOT = 'book-top-slot',
  GET_NO_TOP_SLOTS = 'get-no-top-slots',
  GET_TOP_SLOTS = 'get-top-slots',
  SAVE_NO_SLOT_INFORMATION = 'save-no-slot-information',
}

const EMPTY_GRADE = -1;

export const useGetTopSlotsQuery = ({ hashId, serviceAppointmentId, minDate, maxDate }: GetTopSlotsParams) => {
  const [topSlots, setTopSlots] = useState<SlotsDictionary>({});
  const [areTopSlotsLoaded, setAreTopSlotsLoaded] = useState(false);
  const [availableDateEnd, setAvailableDateEnd] = useState(maxDate);

  const [highestSlot, setHighestSlot] = useState<TopSlot>({ start: '', end: '', grade: EMPTY_GRADE });

  const { data, isLoading } = useQuery<GetTopSlotsResult>({
    queryKey: [TOP_SLOT_QUERY_KEYS.GET_TOP_SLOTS, hashId, serviceAppointmentId, minDate, maxDate],
    queryFn: ({ signal }) => topslotService.getTopSlots(
      hashId || '',
      serviceAppointmentId || '',
      minDate,
      maxDate,
      signal
    ),
    enabled: !!hashId && !!serviceAppointmentId,
  });

  useEffect(() => {
    if (isLoading || !data) {
      setAreTopSlotsLoaded(false);
      return;
    }

    let highestGrade: number = EMPTY_GRADE;
    let newBestSlot: TopSlot | null = null;

    sendAnalyticsSlotsReceived(data.slots);

    const newTopSlots = data.slots.reduce((slots, s) => {
      const timeslots = s.timeslots;
      const newHighest = s.timeslots?.reduce((prev, current) => {
        return prev.grade >= current.grade ? prev : current;
      });

      if (timeslots) {
        slots[s.date] = timeslots;
      }

      if (newHighest) {
        if (highestGrade < newHighest.grade) {
          highestGrade = newHighest.grade;
          newBestSlot = newHighest;
        }
      }

      return slots;
    }, {} as SlotsDictionary);

    setAvailableDateEnd(data.requestedDateEnd);

    setTopSlots((prevTopslots) => ({
      ...prevTopslots,
      ...newTopSlots,
    }));
    setAreTopSlotsLoaded(true);

    if (newBestSlot) {
      setHighestSlot(newBestSlot);
    }

    if (data.slots.length === 0 && data.requestsWithError) {
      sendAnalyticsNoSlotsWithError(data.requestsWithError);
    }
  }, [data, isLoading]);

  return {
    isLoading: isLoading || !areTopSlotsLoaded,
    highestSlot,
    topSlots,
    availableDateEnd,
  };
};

interface BookTopSlotParams {
  appointmentId: string;
  hashId: string;
  selectedTopSlot: TopSlot;
}

export const useBookTopSlotMutation = () => {
  const { mutateAsync, isPending } = useMutation<void, Error, BookTopSlotParams>({
    mutationKey: [TOP_SLOT_QUERY_KEYS.BOOK_TOP_SLOT],
    mutationFn: ({ hashId, appointmentId, selectedTopSlot }: BookTopSlotParams) =>
      topslotService.bookSlot(hashId, appointmentId, selectedTopSlot)
  });

  return {
    bookTopSlot: mutateAsync,
    isPending,
  };
};

interface GetNoTopSlotsParams {
  hashId?: string;
  shouldFetchNoTopSlots: boolean;
  workOrderId?: string;
}

export const useGetNoTopSlotsQuery = ({ hashId, workOrderId, shouldFetchNoTopSlots }: GetNoTopSlotsParams) => {
  const { data, isLoading, refetch } = useQuery<GetNoTopSlotsResult>({
    queryKey: [TOP_SLOT_QUERY_KEYS.GET_NO_TOP_SLOTS, hashId, workOrderId],
    queryFn: () => noTopSlotsService.getNoTopSlots(hashId || '', workOrderId || ''),
    enabled: !!hashId && !!workOrderId && shouldFetchNoTopSlots,
  });

  return {
    getNoTopSlotsResult: data,
    isLoading,
    refetch,
  };
};


export const useSaveNoSlotInfomationMutation = (hashId: string, workOrderId: string) => {
  const { mutateAsync, isPending, isError, reset } = useMutation<void, Error, SaveNoSlotParams>({
    mutationKey: [TOP_SLOT_QUERY_KEYS.SAVE_NO_SLOT_INFORMATION, hashId, workOrderId],
    mutationFn: (params) => topslotService.saveNoSlotInformation(hashId, workOrderId, params),
  });

  return {
    saveNoSlotInformation: mutateAsync,
    reset,
    isPending,
    isError,
  };
};
